/**
 * The gauges the Capital Stack tab draws beside the figures it already prints.
 *
 * Each gauge is the same number as the figure it sits beside, placed on a
 * scale; nothing is computed here that the row does not already state, and a
 * figure the deal cannot support gets no gauge. Two things are presentation
 * and nothing else — the scale a ratio is drawn on (0–100% for a leverage
 * ratio, a rounded ceiling for a yield or a coverage multiple) and the 1.00x
 * break-even tick on DSCR, which is arithmetic (NOI equal to debt service), not
 * a credit threshold. A limit is marked only when the record states it (a
 * covenant), and the mark is named in its hover, never in the row's text.
 */
import type { CovenantGauge, CovenantRow } from './capitalStackModel'
import type { LeverageFigure, LeverageId } from './leverageModel'

/** The figures that have a gauge, in the order the table prints them. */
export type GaugeId = Extract<LeverageId, 'ltv' | 'ltc' | 'debt_yield' | 'cap_rate' | 'dscr' | 'loan_per_unit' | 'basis_per_unit'>

/** How the figure was reached, which the gauge's fill repeats from the chip
 *  beside the label so a derived figure never looks like a stated one. */
export type GaugeState = 'stated' | 'derived' | 'implied' | 'suggested' | 'confirmed'

export type GaugeMark = {
  /** 0–1, where the mark sits on the scale. */
  at: number
  kind: 'break-even' | 'limit'
  /** Said on hover and to assistive tech. */
  title: string
}

export type Gauge = {
  /** 0–1: where the fill ends on the scale (clamped). */
  fill: number
  /** What the scale runs to, printed under its right end ("100%", "12%", "2.0x"). */
  scaleLabel: string
  marks: GaugeMark[]
  /** One sentence for assistive tech and the hover. */
  title: string
}

const PERCENT_STEPS = [6, 8, 10, 12, 15, 20, 25, 30, 40, 50, 75, 100, 150, 200]
const MULTIPLE_STEPS = [2, 2.5, 3, 4, 5, 8, 10, 15, 20]

/** The smallest step that holds `value` with a fifth to spare, so the fill never
 *  runs to the end of its track. */
export function niceCeiling(value: number, steps: readonly number[]): number {
  const target = value * 1.2
  return steps.find((step) => step >= target) ?? Math.ceil(target)
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.min(1, Math.max(0, value))
}

function pointsLabel(max: number): string {
  return `${Number.isInteger(max) ? max : max.toFixed(1)}%`
}

function multipleLabel(max: number): string {
  return `${max.toFixed(1)}x`
}

function usable(figure: LeverageFigure | undefined): figure is LeverageFigure & { value: number } {
  return figure != null && figure.basis != null && typeof figure.value === 'number' && Number.isFinite(figure.value)
}

function limitOf(covenants: CovenantRow[], metric: CovenantGauge['metric']): CovenantGauge | undefined {
  return covenants.find((row) => row.gauge?.metric === metric)?.gauge
}

/** The covenant limit as a mark on a 0..max scale; none when it falls off the scale. */
function limitMark(limit: CovenantGauge | undefined, max: number, text: string): GaugeMark[] {
  if (!limit || !(max > 0) || limit.limit > max) return []
  return [{ at: clamp01(limit.limit / max), kind: 'limit', title: text }]
}

/**
 * The gauges for the figures the Coverage & Sizing table prints.
 *
 * Leverage (LTV, LTC) is a share of a whole, so it runs 0–100%. The two yields
 * share one scale, so the debt yield reads against the cap rate. DSCR runs from
 * zero to a rounded multiple with the 1.00x break-even marked. Loan per unit is
 * drawn against the price (or value) per unit it is a share of.
 */
export function figureGauges(
  figures: Partial<Record<LeverageId, LeverageFigure>>,
  covenants: CovenantRow[] = [],
): Partial<Record<GaugeId, Gauge>> {
  const out: Partial<Record<GaugeId, Gauge>> = {}
  const { ltv, ltc, debt_yield: debtYield, cap_rate: capRate, dscr, loan_per_unit: loanPerUnit, basis_per_unit: basisPerUnit } = figures

  for (const [id, figure, metric] of [['ltv', ltv, 'ltv'], ['ltc', ltc, undefined]] as const) {
    if (!usable(figure) || figure.value < 0) continue
    const max = figure.value > 100 ? niceCeiling(figure.value, PERCENT_STEPS) : 100
    const limit = metric ? limitOf(covenants, metric) : undefined
    out[id] = {
      fill: clamp01(figure.value / max),
      scaleLabel: pointsLabel(max),
      marks: limitMark(limit, max, `Stated limit ${limit?.limit.toFixed(1)}%`),
      title: `${figure.label} ${figure.display} on a scale of 0 to ${pointsLabel(max)}`,
    }
  }

  // The two yields share a scale: the larger of the two, or a stated floor.
  const yields = [debtYield, capRate].filter(usable).filter((figure) => figure.value >= 0)
  if (yields.length > 0) {
    const floor = limitOf(covenants, 'debt_yield')
    const top = Math.max(...yields.map((figure) => figure.value), floor?.limit ?? 0)
    const max = niceCeiling(top, PERCENT_STEPS)
    for (const [id, figure] of [['debt_yield', debtYield], ['cap_rate', capRate]] as const) {
      if (!usable(figure) || figure.value < 0) continue
      out[id] = {
        fill: clamp01(figure.value / max),
        scaleLabel: pointsLabel(max),
        marks: id === 'debt_yield' ? limitMark(floor, max, `Stated floor ${floor?.limit.toFixed(2)}%`) : [],
        title: `${figure.label} ${figure.display} on a scale of 0 to ${pointsLabel(max)}`,
      }
    }
  }

  if (usable(dscr) && dscr.value >= 0) {
    const floor = limitOf(covenants, 'dscr')
    const max = niceCeiling(Math.max(dscr.value, floor?.limit ?? 0, 1), MULTIPLE_STEPS)
    out.dscr = {
      fill: clamp01(dscr.value / max),
      scaleLabel: multipleLabel(max),
      marks: [
        { at: clamp01(1 / max), kind: 'break-even', title: 'Break-even: NOI equal to debt service (1.00x)' },
        ...limitMark(floor, max, `Stated floor ${floor?.limit.toFixed(2)}x`),
      ],
      title: `${dscr.label} ${dscr.display} on a scale of 0 to ${multipleLabel(max)}`,
    }
  }

  // Loan per unit against the price (or value) per unit it is a share of; the
  // price row is the whole the loan row is drawn against.
  if (usable(loanPerUnit) && usable(basisPerUnit) && basisPerUnit.value > 0 && loanPerUnit.value >= 0) {
    out.loan_per_unit = {
      fill: clamp01(loanPerUnit.value / basisPerUnit.value),
      scaleLabel: basisPerUnit.label,
      marks: [],
      title: `${loanPerUnit.label} ${loanPerUnit.display} against ${basisPerUnit.label} ${basisPerUnit.display}`,
    }
    out.basis_per_unit = {
      fill: 1,
      scaleLabel: '',
      marks: [],
      title: `${basisPerUnit.label} ${basisPerUnit.display}: the whole ${loanPerUnit.label} is drawn against`,
    }
  }

  return out
}

/** A covenant's headroom as a gauge: the deal's figure against the limit it is
 *  held to, on the scale of its metric. Null when the record gives no figure. */
export function covenantHeadroom(row: CovenantRow): (Gauge & { limitAt: number; compliant: boolean | null }) | null {
  const g = row.gauge
  if (!g || g.value == null || !Number.isFinite(g.value) || g.value < 0) return null
  const top = Math.max(g.value, g.limit)
  const scale =
    g.metric === 'dscr'
      ? Math.max(2, niceCeiling(top, MULTIPLE_STEPS))
      : g.metric === 'debt_yield'
        ? niceCeiling(top, PERCENT_STEPS)
        : top > 100
          ? niceCeiling(top, PERCENT_STEPS)
          : 100
  return {
    fill: clamp01(g.value / scale),
    scaleLabel: g.metric === 'dscr' ? multipleLabel(scale) : pointsLabel(scale),
    marks: [],
    limitAt: clamp01(g.limit / scale),
    compliant: row.compliant,
    title: `${row.current} against ${row.requirement}`,
  }
}

/** A line's share of its column's total, 0–1; null where there is no total to
 *  take a share of. */
export function shareOf(amount: number, total: number | undefined): number | null {
  if (total == null || !(total > 0) || !Number.isFinite(amount) || amount < 0) return null
  return clamp01(amount / total)
}

/** The state a figure's gauge repeats from its chip. */
export function gaugeStateOf(
  figure: Pick<LeverageFigure, 'implied' | 'basis' | 'field'>,
  lineageState: string | null | undefined,
): GaugeState {
  if (figure.implied) return 'implied'
  if (figure.basis === 'derived' && !figure.field) return 'derived'
  if (lineageState === 'derived' || lineageState === 'suggested' || lineageState === 'confirmed') return lineageState
  return 'stated'
}
